import { $convertFromMarkdownString } from '@lexical/markdown';
import { $isAtNodeEnd } from '@lexical/selection';
import {
  $getRoot,
  $getSelection,
  ElementNode,
  LexicalEditor,
  LexicalNode,
  RangeSelection,
  TextNode,
} from 'lexical';
import { logError } from 'lib/sentry/logError';

import { TEXT_EDITOR_TRANSFORMERS } from './constant';
import {
  convertHtmlToLexicalNodes,
  isHtml,
} from './utils/convertHtmlToLexicalNodes';

export const prepareEditorState = ({ value }: { value: string }) => {
  const isJsonValue = tryParseJson(value);
  if (isJsonValue) {
    return value;
  }

  return (editor: LexicalEditor) => {
    let nodes: LexicalNode[] = [];

    try {
      nodes = convertHtmlToLexicalNodes(editor, reformatLineBreaksIfAny(value));
    } catch (error) {
      logError(error);
    }

    $getRoot().select();
    $getSelection()?.insertNodes(nodes);
  };
};

export const prepareHtmlEditorState =
  ({ value, options }: { value: string; options: { clear: boolean } }) =>
  (editor: LexicalEditor) => {
    let nodes: LexicalNode[] = [];
    try {
      nodes = convertHtmlToLexicalNodes(editor, reformatLineBreaksIfAny(value));
    } catch (error) {
      logError(error);
    }

    options?.clear && $getRoot().clear();
    $getRoot().select();
    $getSelection()?.insertNodes(nodes);
  };

export const prepareMarkdownEditorState = ({ value }: { value: string }) => {
  const isHtmlString = isHtml(value);

  return (editor: LexicalEditor) => {
    if (isHtmlString) {
      try {
        const nodes = convertHtmlToLexicalNodes(
          editor,
          reformatLineBreaksIfAny(value)
        );
        $getRoot().select();
        $getSelection()?.insertNodes(nodes);
      } catch (error) {
        logError(error);
      }
    } else {
      $convertFromMarkdownString(
        value,
        TEXT_EDITOR_TRANSFORMERS,
        undefined,
        true // shouldPreserveNewLines
      );
    }
  };
};

export const tryParseJson = (html: string) => {
  try {
    const json = JSON.parse(html);
    // check if it's really a json object and not just a string
    return typeof json === 'object';
  } catch (error) {
    return false;
  }
};

export const getSelectedNode = (
  selection: RangeSelection
): TextNode | ElementNode => {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? anchorNode : focusNode;
  }
};

const SUPPORTED_URL_PROTOCOLS = new Set([
  'http:',
  'https:',
  'mailto:',
  'sms:',
  'tel:',
]);

export function sanitizeUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);
    // eslint-disable-next-line no-script-url
    if (!SUPPORTED_URL_PROTOCOLS.has(parsedUrl.protocol)) {
      return 'about:blank';
    }
  } catch {
    return url;
  }
  return url;
}

const VERTICAL_GAP = 10;
const HORIZONTAL_OFFSET = 5;

export function setFloatingElemPositionForLinkEditor(
  targetRect: DOMRect | null,
  floatingElem: HTMLElement,
  anchorElem: HTMLElement,
  verticalGap: number = VERTICAL_GAP,
  horizontalOffset: number = HORIZONTAL_OFFSET
): void {
  const scrollerElem = anchorElem.parentElement;

  if (targetRect === null || !scrollerElem) {
    floatingElem.style.opacity = '0';
    floatingElem.style.transform = 'translate(-10000px, -10000px)';
    return;
  }

  const floatingElemRect = floatingElem.getBoundingClientRect();
  const anchorElementRect = anchorElem.getBoundingClientRect();
  const editorScrollerRect = scrollerElem.getBoundingClientRect();

  let top = targetRect.top - verticalGap;
  let left = targetRect.left - horizontalOffset;

  if (top < editorScrollerRect.top) {
    top += floatingElemRect.height + targetRect.height + verticalGap * 2;
  }

  if (left + floatingElemRect.width > editorScrollerRect.right) {
    left = editorScrollerRect.right - floatingElemRect.width - horizontalOffset;
  }

  top -= anchorElementRect.top;
  left -= anchorElementRect.left;

  floatingElem.style.opacity = '1';
  floatingElem.style.transform = `translate(${left}px, ${top}px)`;
}

export function setFloatingElemPosition(
  targetRect: DOMRect | null,
  floatingElem: HTMLElement,
  anchorElem: HTMLElement,
  isLink: boolean = false,
  verticalGap: number = VERTICAL_GAP,
  horizontalOffset: number = HORIZONTAL_OFFSET
): void {
  const scrollerElem = anchorElem.parentElement;

  if (targetRect === null || !scrollerElem) {
    floatingElem.style.opacity = '0';
    floatingElem.style.transform = 'translate(-10000px, -10000px)';
    return;
  }

  const floatingElemRect = floatingElem.getBoundingClientRect();
  const anchorElementRect = anchorElem.getBoundingClientRect();
  const editorScrollerRect = scrollerElem.getBoundingClientRect();

  let top = targetRect.top - floatingElemRect.height - verticalGap;
  let left = targetRect.left - horizontalOffset;

  if (top < editorScrollerRect.top) {
    // adjusted height for link element if the element is at top
    top +=
      floatingElemRect.height +
      targetRect.height +
      verticalGap * (isLink ? 9 : 2);
  }

  if (left + floatingElemRect.width > editorScrollerRect.right) {
    left = editorScrollerRect.right - floatingElemRect.width - horizontalOffset;
  }

  top -= anchorElementRect.top;
  left -= anchorElementRect.left;

  floatingElem.style.opacity = '1';
  floatingElem.style.transform = `translate(${left}px, ${top}px)`;
}

export function getDOMRangeRect(
  nativeSelection: Selection,
  rootElement: HTMLElement
): DOMRect {
  const domRange = nativeSelection.getRangeAt(0);

  let rect;

  if (nativeSelection.anchorNode === rootElement) {
    let inner = rootElement;
    while (inner.firstElementChild != null) {
      inner = inner.firstElementChild as HTMLElement;
    }
    rect = inner.getBoundingClientRect();
  } else {
    rect = domRange.getBoundingClientRect();
  }

  return rect;
}

export const reformatLineBreaksIfAny = (htmlString: string = ''): string => {
  const splitted = htmlString.split(/\n/g);
  if (!splitted.length) {
    return htmlString;
  }

  return htmlString.replace(/\n/g, '<br />');
};
